import { useState, useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Divider, Loader } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { zodResolver } from 'mantine-form-zod-resolver'

import { Metadata, useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import DnPrompt from 'src/components/Core/DnPrompt'
import QuestionRow from 'src/components/Inputs/QuestionRow/QuestionRow'
import { PDFPreviewModal } from 'src/components/Overlays/PDFPreviewModal/PDFPreviewModal'
import SwitchCompanyModal from 'src/components/Overlays/SwitchCompanyModal/SwitchCompanyModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DECLINE_VOE_REQUEST,
  GET_VOE_BY_ID,
  GET_VOE_FILE_DOWNLOAD_URL,
  SUBMIT_VOE_RESPONSE_CONTENT,
} from 'src/graphql/voe.graphql'
import { useVoECompanySwitch } from 'src/hooks/useVoECompanySwitch'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import { hasNeverWorkedHereFinding } from 'src/lib/voeFindings'
import {
  toVoeResponseSubmitInput,
  voeResponseFormSchema,
} from 'src/lib/voeResponseFormSchema'
import { downloadFile } from 'src/utils/downloadFile'

import AccidentsCard from '../ApplicantDetailsPage/tabs/ApplicationTab/components/AccidentsCard'

import AuditTrailCard from './components/AuditTrailCard'
import CompanyInfoCard from './components/CompanyInfoCard'
import DriverInfoCard from './components/DriverInfoCard'
import RequestContentColumn from './components/RequestContentColumn'
import RequestInfoCard from './components/RequestInfoCard'
import ResponseContentColumn from './components/ResponseContentColumn'
import VoEConsentCard from './components/VoEConsentCard'
import VoEDetailsHeader from './components/VoEDetailsHeader'

const VoERequestDetailsPage = ({ id }) => {
  const [openConsent, setOpenConsent] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null)
  const [
    declineModalOpened,
    { open: openDeclineModal, close: closeDeclineModal },
  ] = useDisclosure(false)

  const form = useForm({
    validate: zodResolver(voeResponseFormSchema),
  })

  const [submitVoEResponse, { loading: loadingSubmit }] = useMutation(
    SUBMIT_VOE_RESPONSE_CONTENT,
    {
      refetchQueries: [
        {
          query: GET_VOE_BY_ID,
          variables: {
            id: id,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('VoE response submitted successfully!', 'success')
      },
      onError: (error) => {
        console.log(error)
        const errorMessage =
          error?.message || 'Failed to submit VoE response. Please try again.'
        toast(errorMessage, 'error')
      },
    }
  )

  const [fetchDownloadUrl] = useLazyQuery(GET_VOE_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
  })

  const [declineVoERequest, { loading: loadingDecline }] = useMutation(
    DECLINE_VOE_REQUEST,
    {
      refetchQueries: [
        {
          query: GET_VOE_BY_ID,
          variables: {
            id: id,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        closeDeclineModal()
        toast('VoE request declined successfully!', 'success')
      },
      onError: (error) => {
        console.log(error)
        const errorMessage =
          error?.message || 'Failed to decline VoE request. Please try again.'
        toast(errorMessage, 'error')
      },
    }
  )

  const {
    handleVoELoadError,
    switchTarget,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  } = useVoECompanySwitch(id)

  const { data, loading } = useQuery(GET_VOE_BY_ID, {
    variables: {
      id: id,
    },
    onCompleted: () => {
      handleInitialValues()
    },
    onError: (error) => {
      handleVoELoadError(error)
    },
  })

  const voe = data?.verificationOfEmployment

  const handleSubmit = (values) => {
    submitVoEResponse({
      variables: {
        id,
        input: toVoeResponseSubmitInput(values),
      },
    })
  }

  const handleInitialValues = () => {
    if (!voe) return

    form.setValues(
      voe.status === 'COMPLETED'
        ? {
            positionHeld: voe.responseContent?.positionHeld,
            startDate: voe.responseContent?.startDate
              ? new Date(voe.responseContent.startDate)
              : null,
            endDate: voe.responseContent?.endDate
              ? new Date(voe.responseContent.endDate)
              : null,
            reasonForLeaving: voe.responseContent?.reasonForLeaving,
            trailerTypes: voe.responseContent?.trailerTypes,
            eligibleForRehire: voe.responseContent?.eligibleForRehire || false,
            isDriverTerminated: voe.responseContent?.isDriverTerminated,
            isDriverSubjectToFMCSRs:
              voe.responseContent?.isDriverSubjectToFMCSRs,
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              voe.responseContent
                ?.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
            accidents: voe.accidents,
          }
        : {
            accidents: voe.accidents,
            positionHeld: voe.requestContent?.positionHeld,
            startDate: voe.requestContent?.startDate
              ? new Date(voe.requestContent.startDate)
              : null,
            endDate: voe.requestContent?.endDate
              ? new Date(voe.requestContent.endDate)
              : null,
            reasonForLeaving: voe.requestContent?.reasonForLeaving,
            trailerTypes: voe.requestContent?.trailerTypes,
            eligibleForRehire: voe.requestContent?.eligibleForRehire || false,
            isDriverTerminated: voe.requestContent?.isDriverTerminated,
            isDriverSubjectToFMCSRs:
              voe.requestContent?.isDriverSubjectToFMCSRs,
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              voe.requestContent
                ?.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
          }
    )
  }

  const isCompleted = voe?.status === 'COMPLETED'
  const isDeclined = voe?.status === 'DECLINED'
  const neverWorkedHere = hasNeverWorkedHereFinding(voe?.auditTrailLogs)

  const handleDecline = async () => {
    await declineVoERequest({
      variables: {
        id,
      },
    })
  }

  const handleAccidentsUpdate = async ({ accidents }) => {
    form.setFieldValue(`accidents`, accidents)
    return { errors: null }
  }

  const onPreviewDriverConsent = useCallback(() => {
    if (!voe?.driverConsentFileUrl) {
      toast('Consent file not available', 'error')
      return
    }
    setOpenConsent(true)
  }, [voe?.driverConsentFileUrl])

  const onConsentDownload = useCallback(() => {
    downloadFile({
      url: voe?.driverConsentFileUrl,
      fileName: 'consent.pdf',
    })
  }, [voe?.driverConsentFileUrl])

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (!voe) {
    return (
      <>
        <SwitchCompanyModal
          opened={!!switchTarget}
          applicantName={switchTarget?.driverName ?? ''}
          companyName={switchTarget?.companyName ?? ''}
          isSwitching={isSwitching}
          onCancel={cancelSwitch}
          onConfirm={confirmSwitch}
        />
        <div className="flex h-full flex-col items-center justify-center">
          <div className="text-lg text-doubleNickel-gray-600">
            VoE data not found
          </div>
        </div>
      </>
    )
  }

  return (
    <form
      className="flex h-full flex-col gap-6  overflow-auto px-6 pb-10"
      onSubmit={form.onSubmit((values) => handleSubmit(values))}
    >
      <Metadata
        title="IncomingVoEDetails"
        description="IncomingVoEDetails page"
      />
      <VoEDetailsHeader
        showSubmitButton={!isCompleted && !isDeclined}
        showDeclineButton={!isCompleted && !isDeclined}
        loadingSubmit={loadingSubmit}
        loadingDecline={loadingDecline}
        onDecline={openDeclineModal}
        voe={voe}
      />
      {neverWorkedHere && (
        <div className="flex flex-row items-start gap-3 rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-25 px-4 py-3">
          <IconAlertCircle className="mt-0.5 shrink-0 fill-none stroke-doubleNickel-warning-600" />
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-semibold text-doubleNickel-gray-900">
              Employer says the driver never worked there
            </div>
            <div className="text-xs text-doubleNickel-gray-600">
              No employment details were provided. Review before completing or
              declining this request.
            </div>
          </div>
        </div>
      )}
      <RequestInfoCard voe={voe} />
      <CompanyInfoCard voe={voe} />
      <DriverInfoCard voe={voe} noApplicantLink={false} />

      {/* REQUEST CONTENT */}
      <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
        <div className="flex flex-col gap-6 p-6">
          <div className="text-md font-semibold text-doubleNickel-gray-900">
            Request Content
          </div>
          <div className="flex flex-row gap-8 ">
            <RequestContentColumn form={form} request={voe?.requestContent} />
            <Divider orientation="vertical" />
            <ResponseContentColumn
              editing={!isCompleted && !isDeclined}
              form={form}
              request={voe?.requestContent}
            />
          </div>
          <div className="flex flex-row items-center gap-4 rounded-lg bg-doubleNickel-brand-25 px-4 py-3">
            <IconAlertCircle className="fill-none stroke-doubleNickel-brand-500" />
            <div className="flex-1">
              <QuestionRow
                required
                text="Is this applicant eligible for rehire?"
                value={
                  isCompleted
                    ? voe.responseContent?.eligibleForRehire
                    : form.values.eligibleForRehire
                }
                error={form.errors.eligibleForRehire as string | undefined}
                handleChange={(value) =>
                  form.setFieldValue(`eligibleForRehire`, value === 'true')
                }
                editing={!isCompleted && !isDeclined}
              />
            </div>
          </div>
        </div>
      </div>
      {form.values.accidents && (
        <AccidentsCard
          values={form.values.accidents}
          handleUpdateApplication={handleAccidentsUpdate}
          readOnly={isCompleted || isDeclined}
        />
      )}
      <VoEConsentCard
        onPreview={onPreviewDriverConsent}
        loading={false}
        documents={voe.documents || []}
        onDocumentClick={async (doc) => {
          if (doc.voeDocumentType === 'CONSENT_PDF') {
            if (!voe.driverConsentFileUrl) {
              toast('Consent file not available', 'error')
              return
            }
            onPreviewDriverConsent()
          } else if (doc.voeDocumentType === 'RESPONSE_PDF') {
            try {
              const { data } = await fetchDownloadUrl({
                variables: { voeId: voe.voeId, fileType: 'response' },
              })
              if (data?.voeFileDownloadUrl) {
                setPreviewDoc({
                  fileName: doc.fileName || 'VoE Response',
                  url: data.voeFileDownloadUrl,
                })
              } else {
                toast('File not available', 'error')
              }
            } catch {
              toast('Error loading document', 'error')
            }
          }
        }}
      />

      {openConsent && voe.driverConsentFileUrl && (
        <PDFPreviewModal
          fileName="Driver consent"
          url={voe.driverConsentFileUrl}
          isOpen={openConsent}
          onClose={() => setOpenConsent(null)}
          onBack={() => setOpenConsent(null)}
          onDownload={onConsentDownload}
        />
      )}

      {previewDoc && (
        <PDFPreviewModal
          fileName={previewDoc.fileName}
          url={previewDoc.url}
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          onBack={() => setPreviewDoc(null)}
          onDownload={() =>
            downloadFile({
              url: previewDoc.url,
              fileName: previewDoc.fileName,
            })
          }
        />
      )}

      <AuditTrailCard auditTrailLogs={voe.auditTrailLogs} />

      {!isCompleted && !isDeclined && (
        <div className="flex w-full flex-row justify-end gap-3">
          <Button
            text="Decline"
            variant="outline"
            color="red"
            onClick={openDeclineModal}
            disabled={loadingSubmit || loadingDecline}
          />
          {!loadingSubmit && <Button text="Submit" type="submit" />}
          {loadingSubmit && <Loader size="sm" />}
        </div>
      )}

      <DnPrompt
        opened={declineModalOpened}
        close={closeDeclineModal}
        title="Decline VoE Request"
        description={`Are you sure you want to decline this VoE request for ${voe?.driverName}? The requesting party will be notified that this driver was never employed by your company.`}
        onConfirm={handleDecline}
        confirmText="Decline Request"
        cancelText="Cancel"
        variant="warning"
      />
    </form>
  )
}

export default VoERequestDetailsPage
